import { Drawer, FormLabel, Grid, IconButton } from '@mui/material';
import CustomButton from '../../../Components/CustomButton';
import { useEffect, useState } from 'react';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import '../../../styles/filter.scss';
import { useDispatch } from 'react-redux';
import {
  setFilterApplied,
  updatePaginationPage,
} from '../../../redux/commonSlice';
import Images from '../../../Utils/images';
import ScrollableAutocomplete from './ScrollableAutocomplete';
import { useListing } from '../../../hooks/useListing';
import endPoints from '../../../api/endpoint';
import { useParams, useSearchParams } from 'react-router-dom';

interface FilterProps {
  filter?: any;
  setFilter?: any;
  productionType?: any;
  filterConfig?: {
    disabled: string[];
    prefilled: string[];
  };
  entityScope?: { entityType?: string; entityId?: string };
  prefillEntityData?: { entityType: string; entity: any };
}

export default function MyDocumentFilter({
  filter,
  setFilter,
  productionType,
  filterConfig,
  entityScope,
  prefillEntityData,
}: FilterProps) {
  const { id } = useParams();
  const dispatch: any = useDispatch();
  const [searchParams] = useSearchParams();

  const folderType = searchParams.get('folderType');

  const [open, setOpen] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<any[]>([]);
  const [selectedEpisodes, setSelectedEpisodes] = useState<any[]>([]);
  const [selectedProductions, setSelectedProductions] = useState<any[]>([]);
  const [selectedSets, setSelectedSets] = useState<any[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [pageEpisodes, setPageEpisodes] = useState(1);
  const [pageSets, setPageSets] = useState(1);
  const [pageLocations, setPageLocations] = useState(1);
  const [pageDepartments, setPageDepartments] = useState(1);
  const [pageProductions, setPageproductions] = useState(1);

  const [tempSelectedEpisodes, setTempSelectedEpisodes] = useState<any[]>([]);
  const [tempSelectedSets, setTempSelectedSets] = useState<any[]>([]);
  const [tempSelectedLocations, setTempSelectedLocations] = useState<any[]>([]);
  const [tempSelectedDepartments, setTempSelectedDepartments] = useState<any[]>(
    [],
  );
  const [tempSelectedProductions, setTempSelectedProductions] = useState<any[]>(
    [],
  );

  const prefillEntityId: string | null =
    prefillEntityData?.entity?._id &&
    filterConfig?.prefilled.includes(prefillEntityData.entityType)
      ? prefillEntityData.entity._id
      : null;
  const prefillEntityType: string | null = prefillEntityId
    ? prefillEntityData?.entityType ?? null
    : null;

  const manualSelections = {
    productions:
      prefillEntityType === 'production'
        ? tempSelectedProductions.filter((p: any) => p._id !== prefillEntityId)
        : tempSelectedProductions,
    episodes:
      prefillEntityType === 'episode'
        ? tempSelectedEpisodes.filter((e: any) => e._id !== prefillEntityId)
        : tempSelectedEpisodes,
    sets:
      prefillEntityType === 'set'
        ? tempSelectedSets.filter((s: any) => s._id !== prefillEntityId)
        : tempSelectedSets,
    locations:
      prefillEntityType === 'location'
        ? tempSelectedLocations.filter((l: any) => l._id !== prefillEntityId)
        : tempSelectedLocations,
    departments:
      prefillEntityType === 'department'
        ? tempSelectedDepartments.filter((d: any) => d._id !== prefillEntityId)
        : tempSelectedDepartments,
  };

  const isApplyDisabled =
    !manualSelections.productions.length &&
    !manualSelections.episodes.length &&
    !manualSelections.sets.length &&
    !manualSelections.locations.length &&
    !manualSelections.departments.length;

  const isEntityDisabled = (type: string) =>
    filterConfig?.disabled.includes(type) ?? false;

  // Base model entity params from the current banner/context scope.
  const baseModelParams =
    entityScope?.entityType && entityScope?.entityId
      ? {
          modelEntityType: entityScope.entityType,
          modelEntityId: entityScope.entityId,
        }
      : {};

  // Ancestor-selection flags (disabled/prefilled selections don't count).
  const productionSelected =
    !isEntityDisabled('production') && tempSelectedProductions.length > 0;
  const episodeSelected =
    !isEntityDisabled('episode') && tempSelectedEpisodes.length > 0;
  const setSelected =
    !isEntityDisabled('set') && tempSelectedSets.length > 0;
  const locationSelected =
    !isEntityDisabled('location') && tempSelectedLocations.length > 0;

  // Cascading suppression: if ANY ancestor in the hierarchy is selected, drop
  // modelEntityType/Id for that entity and all its descendants — the selected
  // ancestor IDs (productionIds, episodeIds, …) already scope the results.

  // Episode list — suppress if production is selected
  const episodeModelParams = productionSelected ? {} : baseModelParams;

  // Set list — suppress if production OR episode is selected
  const setModelParams =
    productionSelected || episodeSelected ? {} : baseModelParams;

  // Location list — suppress if production, episode, OR set is selected
  const locationModelParams =
    productionSelected || episodeSelected || setSelected ? {} : baseModelParams;

  // Department list — suppress if production, episode, set, OR location is selected
  const departmentModelParams =
    productionSelected || episodeSelected || setSelected || locationSelected
      ? {}
      : baseModelParams;

  // Disabled entity types must not leak their IDs into sibling filter API calls.
  const explicitProductionIds = isEntityDisabled('production')
    ? ''
    
    : tempSelectedProductions.map((item: any) => item._id).join(',');

  const episodeIdsParam = isEntityDisabled('episode')
    ? ''
    : tempSelectedEpisodes?.map((item: any) => item._id).join(',');

  const setIdsParam = isEntityDisabled('set')
    ? ''
    : tempSelectedSets?.map((item: any) => item._id).join(',');

  const locationIdsParam = isEntityDisabled('location')
    ? ''
    : tempSelectedLocations?.map((item: any) => item._id).join(',');

  const {
    fetchListing: fetchEpisodes,
    data: episodeList,
    hasMore: hasMoreEpisodes,
    loading: loadingEpisodes,
  } = useListing<any>({
    url: endPoints.myDocuments.filterList,
    params: {
      page: pageEpisodes,
      entityType: 'episodes',
      limit: 500,
      productionIds: explicitProductionIds,
      ...episodeModelParams,
    },
    isInfinite: true,
    isFilter: true,
  });

  const {
    fetchListing: fetchSets,
    data: setList,
    hasMore: hasMoreSets,
    loading: loadingSets,
  } = useListing<any>({
    url: endPoints.myDocuments.filterList,
    params: {
      page: pageSets,
      entityType: 'sets',
      limit: 500,
      productionIds: explicitProductionIds,
      episodeIds: episodeIdsParam,
      ...setModelParams,
    },
    isInfinite: true,
    isFilter: true,
  });

  const {
    fetchListing: fetchLocations,
    data: locationList,
    hasMore: hasMoreLocations,
    loading: loadingLocations,
  } = useListing<any>({
    url: endPoints.myDocuments.filterList,
    params: {
      page: pageLocations,
      entityType: 'locations',
      limit: 500,
      productionIds: explicitProductionIds,
      episodeIds: episodeIdsParam,
      setIds: setIdsParam,
      ...locationModelParams,
    },
    isInfinite: true,
    isFilter: true,
  });

  const {
    fetchListing: fetchDepartments,
    data: departmentList,
    hasMore: hasMoreDepartments,
    loading: loadingDepartments,
  } = useListing<any>({
    url: endPoints.myDocuments.filterList,
    params: {
      page: pageDepartments,
      entityType: 'departments',
      limit: 500,
      productionIds: explicitProductionIds,
      episodeIds: episodeIdsParam,
      setIds: setIdsParam,
      locationIds: locationIdsParam,
      ...departmentModelParams,
    },
    isInfinite: true,
    isFilter: true,
  });
  const {
    fetchListing: fetchProduction,
    data: productionList,
    hasMore: hasMoreProduction,
    loading: loadingProduction,
  } = useListing<any>({
    url: endPoints.myDocuments.filterList,
    params: {
      page: pageProductions,
      entityType: 'productions',
      limit: 500,
      // productionId: id ? id : '',
      ...(entityScope?.entityType && entityScope?.entityId
        ? {
            modelEntityType: entityScope.entityType,
            modelEntityId: entityScope.entityId,
          }
        : {}),
    },
    isInfinite: true,
    isFilter: true,
  });

  const handleReset = () => {
    updatePaginationPage(1);
    setSelectedDepartments([]);
    setSelectedEpisodes([]);
    setSelectedSets([]);
    setSelectedLocations([]);
    setSelectedProductions([]);
    setIsFilterApplied(false);
    dispatch(setFilterApplied(false));
    setFilter({
      page: 1,
      limit: 10,
    });
    setTimeout(() => setOpen(false), 300);
  };

  const handleApply = () => {
    setSelectedEpisodes(manualSelections.episodes);
    setSelectedSets(manualSelections.sets);
    setSelectedLocations(manualSelections.locations);
    setSelectedDepartments(manualSelections.departments);
    setSelectedProductions(manualSelections.productions);

    setIsFilterApplied(true);
    dispatch(setFilterApplied(true));
    updatePaginationPage(1);
    setTimeout(() => setOpen(false), 300);

    setFilter({
      ...filter,
      page: 1,
      episodes: manualSelections.episodes
        .map((item: any) => item._id)
        .join(','),
      sets: manualSelections.sets.map((item: any) => item._id).join(','),
      locations: manualSelections.locations
        .map((item: any) => item._id)
        .join(','),
      departments: manualSelections.departments
        .map((item: any) => item._id)
        .join(','),
      productions: manualSelections.productions
        .map((item: any) => item._id)
        .join(','),
    });
  };

  const handleScroll = (
    event: React.UIEvent<HTMLUListElement>,
    entity: 'episodes' | 'sets' | 'locations' | 'departments' | 'productions',
  ) => {
    const listboxNode = event.currentTarget;
    const scrollTop = listboxNode.scrollTop;
    const scrollHeight = listboxNode.scrollHeight;
    const clientHeight = listboxNode.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 1) {
      switch (entity) {
        case 'episodes':
          if (!loadingEpisodes && hasMoreEpisodes)
            setPageEpisodes(prev => prev + 1);
          break;
        case 'sets':
          if (!loadingSets && hasMoreSets) setPageSets(prev => prev + 1);
          break;
        case 'locations':
          if (!loadingLocations && hasMoreLocations)
            setPageLocations(prev => prev + 1);
          break;
        case 'departments':
          if (!loadingDepartments && hasMoreDepartments)
            setPageDepartments(prev => prev + 1);
          break;
        case 'productions':
          if (!loadingProduction && hasMoreProduction)
            setPageproductions(prev => prev + 1);
          break;
      }
    }
  };

  useEffect(() => {
    setSelectedDepartments([]);
    setSelectedEpisodes([]);
    setSelectedSets([]);
    setSelectedLocations([]);
    setSelectedProductions([]);
    setTempSelectedDepartments([]);
    setTempSelectedEpisodes([]);
    setTempSelectedSets([]);
    setTempSelectedLocations([]);
    setTempSelectedProductions([]);
    setIsFilterApplied(false);
    dispatch(setFilterApplied(false));
    if (setFilter) {
      setFilter((prev: any) => ({
        ...prev,
        page: 1,
        episodes: '',
        sets: '',
        locations: '',
        departments: '',
        productions: '',
      }));
    }
  }, [id]);

  useEffect(() => {
    if (!isEntityDisabled('episode')) fetchEpisodes();
  }, [pageEpisodes, tempSelectedProductions]);

  useEffect(() => {
    if (!isEntityDisabled('set')) fetchSets();
  }, [pageSets, tempSelectedProductions, tempSelectedEpisodes]);

  useEffect(() => {
    if (!isEntityDisabled('location')) fetchLocations();
  }, [
    pageLocations,
    tempSelectedProductions,
    tempSelectedEpisodes,
    tempSelectedSets,
  ]);

  useEffect(() => {
    fetchDepartments();
  }, [
    pageDepartments,
    tempSelectedProductions,
    tempSelectedEpisodes,
    tempSelectedSets,
    tempSelectedLocations,
  ]);

  useEffect(() => {
    if ((!folderType || folderType === 'studio') && !isEntityDisabled('production')) {  
      fetchProduction();
    }
  }, [pageProductions]);

  console.log(productionType,"ebjfvekevfv")

  return (
    <div className="reelFilter">
      <CustomButton
        onClick={() => {
          let initEpisodes = [...selectedEpisodes];
          let initSets = [...selectedSets];
          let initLocations = [...selectedLocations];
          let initDepartments = [...selectedDepartments];
          let initProductions = [...selectedProductions];

          if (prefillEntityId && prefillEntityData?.entity) {
            const { entity } = prefillEntityData;
            if (
              prefillEntityType === 'production' &&
              !initProductions.some((p: any) => p._id === prefillEntityId)
            ) {
              initProductions = [entity, ...initProductions];
            } else if (
              prefillEntityType === 'episode' &&
              !initEpisodes.some((e: any) => e._id === prefillEntityId)
            ) {
              initEpisodes = [entity, ...initEpisodes];
            } else if (
              prefillEntityType === 'set' &&
              !initSets.some((s: any) => s._id === prefillEntityId)
            ) {
              initSets = [entity, ...initSets];
            } else if (
              prefillEntityType === 'location' &&
              !initLocations.some((l: any) => l._id === prefillEntityId)
            ) {
              initLocations = [entity, ...initLocations];
            } else if (
              prefillEntityType === 'department' &&
              !initDepartments.some((d: any) => d._id === prefillEntityId)
            ) {
              initDepartments = [entity, ...initDepartments];
            }
          }

          setTempSelectedEpisodes(initEpisodes);
          setTempSelectedSets(initSets);
          setTempSelectedLocations(initLocations);
          setTempSelectedDepartments(initDepartments);
          setTempSelectedProductions(initProductions);
          setOpen(true);
        }}
        size="large"
        variant="outlined"
        text="Filter"
        showIcon={true}
        width="auto"
        type="button"
        id="filter"
        loading={false}
        iconPosition="start"
        icon={Images.FILTER}
        className={isFilterApplied ? 'ifFilterApplied lg-height' : 'lg-height'}
      />

      <Drawer
        anchor="right"
        open={open}
        onClose={() => {
          setTempSelectedEpisodes(selectedEpisodes);
          setTempSelectedSets(selectedSets);
          setTempSelectedLocations(selectedLocations);
          setTempSelectedDepartments(selectedDepartments);
          setTempSelectedProductions(selectedProductions);
          setOpen(false);
        }}
        transitionDuration={500}
      >
        <div className="filterWrapper">
          <div className="filterHead">
            <h3>Filter by:</h3>
            <IconButton onClick={() => setOpen(false)}>
              <HighlightOffOutlinedIcon />
            </IconButton>
          </div>

          <div className="filterBody">
            <Grid container spacing={2}>
              {/* Production */}
              {(!folderType || folderType === 'studio') &&
                !filterConfig?.disabled.includes('production') && (
                  <Grid item xs={12}>
                    <div className="form-in lg">
                      <FormLabel className="MuiTypography-subtitle1 text-black fw400 mb-8 d-flex">
                        Projects
                      </FormLabel>
                      <ScrollableAutocomplete
                        value={tempSelectedProductions}
                        onChange={newValue => {
                          setTempSelectedProductions(newValue);
                          setTempSelectedEpisodes([]);
                          setTempSelectedSets([]);
                          setTempSelectedLocations([]);
                          setTempSelectedDepartments([]);
                        }}
                        disabled={filterConfig?.disabled.includes('production')}
                        options={productionList || []}
                        keyName="name"
                        multiple
                        placeholder="Select Productions"
                        loading={loadingProduction}
                        onScroll={e => handleScroll(e, 'productions')}
                        showAddOption={false}
                      />
                    </div>
                  </Grid>
                )}

              {/* Episodes */}
              {((!productionType || productionType === 'undefined' )|| productionType === 'TV Series') &&
                !filterConfig?.disabled.includes('episode') && (
                  <Grid item xs={12}>
                    <div className="form-in lg">
                      <FormLabel className="MuiTypography-subtitle1 text-black fw400 mb-8 d-flex">
                        Episodes/Events
                      </FormLabel>

                      <ScrollableAutocomplete
                        value={tempSelectedEpisodes}
                        onChange={newValue => {
                          setTempSelectedEpisodes(newValue);
                          setTempSelectedSets([]);
                          setTempSelectedLocations([]);
                          setTempSelectedDepartments([]);
                        }}
                        disabled={filterConfig?.disabled.includes('episode')}
                        options={episodeList || []}
                        keyName="name"
                        multiple
                        isEpisode
                        placeholder="Select Episodes"
                        loading={loadingEpisodes}
                        onScroll={e => handleScroll(e, 'episodes')}
                        showAddOption={false}
                      />
                    </div>
                  </Grid>
                )}

              {/* Sets */}
              {!filterConfig?.disabled.includes('set') && (
                <Grid item xs={12}>
                  <div className="form-in lg">
                    <FormLabel className="MuiTypography-subtitle1 text-black fw400 mb-8 d-flex">
                      Sets/Activity
                    </FormLabel>

                    <ScrollableAutocomplete
                      value={tempSelectedSets}
                      // onChange={setTempSelectedSets}
                      onChange={newValue => {
                        setTempSelectedSets(newValue);
                        setTempSelectedLocations([]);
                        setTempSelectedDepartments([]);
                      }}
                      options={(setList || []).map((set: any) => ({
                        ...set,
                        name: set.name?.toUpperCase(),
                      }))}
                      disabled={filterConfig?.disabled.includes('set')}
                      keyName="name"
                      multiple
                      placeholder="Select Sets/Activity"
                      loading={loadingSets}
                      onScroll={e => handleScroll(e, 'sets')}
                      showAddOption={false}
                    />
                  </div>
                </Grid>
              )}

              {/* Locations */}
              {!filterConfig?.disabled.includes('location') && (
                <Grid item xs={12}>
                  <div className="form-in lg">
                    <FormLabel className="MuiTypography-subtitle1 text-black fw400 mb-8 d-flex">
                      Locations
                    </FormLabel>

                    <ScrollableAutocomplete
                      value={tempSelectedLocations}
                      // onChange={setTempSelectedLocations}
                      onChange={newValue => {
                        setTempSelectedLocations(newValue);
                        setTempSelectedDepartments([]);
                      }}
                      options={locationList || []}
                      keyName={'address'}
                      disabled={filterConfig?.disabled.includes('location')}
                      multiple
                      placeholder="Select Locations"
                      loading={loadingLocations}
                      onScroll={e => handleScroll(e, 'locations')}
                      showAddOption={false}
                    />
                  </div>
                </Grid>
              )}

              {/* Departments */}
              <Grid item xs={12}>
                <div className="form-in lg">
                  <FormLabel className="MuiTypography-subtitle1 text-black fw400 mb-8 d-flex">
                    Departments
                  </FormLabel>

                  <ScrollableAutocomplete
                    value={tempSelectedDepartments}
                    onChange={setTempSelectedDepartments}
                    options={departmentList || []}
                    keyName="deptName"
                    multiple
                    placeholder="Select Departments"
                    disabled={filterConfig?.disabled.includes('department')}
                    loading={loadingDepartments}
                    onScroll={e => handleScroll(e, 'departments')}
                    showAddOption={false}
                  />
                </div>
              </Grid>
            </Grid>
          </div>

          <div className="filterFooter">
            <CustomButton
              onClick={handleReset}
              size="large"
              variant="outlined"
              text="Reset"
              showIcon={false}
              width="100%"
              type="button"
              id="reset"
              loading={false}
            />
            <CustomButton
              onClick={handleApply}
              size="large"
              variant="contained"
              text="Apply"
              showIcon={false}
              width="100%"
              type="button"
              id="apply"
              disabled={isApplyDisabled} // <-- disable until any filter selected
            />
          </div>
        </div>
      </Drawer>
    </div>
  );
}
